/**
 * Helper functions and hooks for managing argument table display and interactions
 * in the variant interpretation workflow.
 */

import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox, TableProps } from 'antd';
import { ColumnsType } from 'antd/es/table';
import DOMPurify from 'dompurify';
import React, { useMemo } from 'react';

import { boldColValueRenderer } from '../../../../foundation/utils/boldColValueRenderer';
import { filterArgumentsByStage, StageFilterOptions } from '../../utils';
import EvidenceDisplay from '../EvidenceDisplay';
import {
  CLASSIFICATION_STAGE_DISPLAY_NAMES,
  StructuredArgument,
} from './structureArgumentsData';

/**
 * Handles argument selection with mutual exclusion for same section + ACMG rule.
 * When selecting an argument, deselects all other arguments in the same section
 * with the same ACMG rule code.
 */
export const handleArgumentSelection = (
  record: StructuredArgument,
  setInternalSource: React.Dispatch<React.SetStateAction<StructuredArgument[]>>,
) => {
  setInternalSource((prevSource) => {
    const isSelecting = !record.selected;
    return prevSource.map((item) => {
      // same section + same ACMG rule → deselect all others automatically
      if (
        isSelecting &&
        item.section === record.section &&
        item.acmg_rule_code === record.acmg_rule_code &&
        item.id !== record.id
      ) {
        return { ...item, selected: false, is_argument_met: false };
      }

      // toggle the clicked item
      if (item.id === record.id) {
        return {
          ...item,
          selected: isSelecting,
          is_argument_met: isSelecting,
        };
      }

      return item;
    });
  });
};

/**
 * Generates row properties for the arguments table based on the record's position and type
 * @param record - The structured argument record
 * @returns Object containing row properties and data attributes for styling and interaction
 */
export const handleRowProps = (record: StructuredArgument) => ({
  onClick: () => {
    // dummy click handler to satisfy TypeScript
  },
  'data-is-stage': record.isStageRow ? 'true' : undefined,
  'data-first-in-section': record.isFirstInSection ? 'true' : undefined,
  'data-last-in-section': record.isLastInSection ? 'true' : undefined,
  'data-section-group':
    !record.isStageRow && !record.isFirstInSection && !record.isLastInSection
      ? 'true'
      : undefined,
});

/**
 * Custom hook that generates table columns configuration for the arguments table
 * @param isClassificationModalOpen - Whether the classification modal is currently open
 * @param showMore - Whether to show additional details for each argument
 * @param handleArgumentSelection - Callback function for when an argument is selected
 * @param handleArgumentEditClick - Callback function for when the edit button is clicked
 * @returns Array of column configurations for the Ant Design table
 */
export const useArgumentsTableColumns = (
  isClassificationModalOpen: boolean,
  showMore: boolean,
  handleArgumentSelection: (record: StructuredArgument) => void,
  handleArgumentEditClick: (record: StructuredArgument) => void,
  isIndividualStageArgument?: boolean,
): ColumnsType<StructuredArgument> => {
  const columns = useMemo(() => {
    const columns: TableProps<StructuredArgument>['columns'] = [
      // Arguments column
      {
        title: 'Arguments',
        key: 'arguments',
        render: (_, record) => {
          if (record.isStageRow && !isIndividualStageArgument) {
            return null;
          } else if (record.isStageRow && isIndividualStageArgument) {
            return (
              <div className="interpretation__stage-section-col">
                <div>{boldColValueRenderer(record.section)}</div>
              </div>
            );
          }

          return (
            <div
              className={`${isIndividualStageArgument ? 'argument-text-container--stage-argument' : ''}`}
            >
              <div className="argument-text-container">
                <div>
                  {isClassificationModalOpen ? (
                    <Checkbox
                      checked={record.selected}
                      onChange={() => handleArgumentSelection(record)}
                    />
                  ) : null}
                </div>
                <div
                  className={`argument-text ${
                    record.selected ? 'argument-text--selected' : ''
                  } ${isClassificationModalOpen ? 'argument-text--modal' : ''}`}
                >
                  {record.selected
                    ? boldColValueRenderer(record.text)
                    : record.text}
                </div>
              </div>
              {record.selection_type_creation_date && (
                <div className="argument-selection-type-date">
                  <span
                    className={`argument-selection-type-date__tag argument-selection-type-date__tag--${record.selection_type === 'auto' ? 'auto' : 'manual'}`}
                  >
                    {record.selection_type_creation_date}
                  </span>
                </div>
              )}
              {(showMore || isIndividualStageArgument) &&
                record.evidencedata &&
                Object.keys(record.evidencedata).length > 0 && (
                  <EvidenceDisplay evidence={record.evidencedata} />
                )}
              {/* Show additional details when showMore is true or in classification modal */}
              {(showMore || isClassificationModalOpen) && (
                <div className="argument-comment-wrapper">
                  {record.comment && record.comment !== 'None' && (
                    <div
                      className="argument-comment"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(record.comment),
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          );
        },
      },
      // Classification column
      {
        title: 'Classification',
        key: 'classification',
        width: 220,
        render: (_, record) => {
          if (record.isStageRow) {
            return null;
          }
          return (
            <span>
              <span
                className={` classification-margin-right ${
                  record.selected
                    ? `classification-${record.acmg_class.toLowerCase()}`
                    : 'classification-text'
                }`}
              >
                {record.acmg_class.toLowerCase()}
              </span>
              <span
                className={` ${
                  record.selected
                    ? `classification-${record.acmg_class.toLowerCase()}`
                    : 'classification-text'
                }`}
              >
                {record.acmg_grade.toLowerCase()}
              </span>
            </span>
          );
        },
      },
      // ACMG/AMP rules column
      {
        title: 'ACMG/AMP rules',
        key: 'rules',
        width: 140,
        render: (_, record) => {
          if (record.isStageRow) {
            return null;
          }
          return (
            <span
              className={`
                  interpretation__classification-rule-code
              ${record.selected ? ' interpretation__classification-rule-code--selected' : ''}

              `}
            >
              {record.acmg_rule_code}
            </span>
          );
        },
      },
    ];

    if (isClassificationModalOpen) {
      columns.push(
        // Action column (edit button)
        {
          title: '',
          key: 'action',
          width: 80,
          render: (_, record) => {
            if (record.isStageRow) {
              return null;
            }
            return (
              <span
                className="interpretation__classification-action-icon"
                onClick={() => handleArgumentEditClick(record)}
              >
                <FontAwesomeIcon icon={faEdit} />
              </span>
            );
          },
        },
      );
    }

    if (!isIndividualStageArgument) {
      columns.unshift(
        // Stage and Section column
        {
          title: 'Stage, Section',
          key: 'stageSection',
          width: 200,
          render: (_, record) => (
            <div className="interpretation__stage-section-col">
              {record.isStageRow && (
                <>
                  {isIndividualStageArgument ? (
                    <div> {boldColValueRenderer(record.section)}</div>
                  ) : (
                    <div> {boldColValueRenderer(record.stage)}</div>
                  )}
                </>
              )}
              {!isIndividualStageArgument &&
                !record.isStageRow &&
                record.isFirstInSection &&
                record.section && (
                  <div className="interpretation__stage-section-value">
                    {boldColValueRenderer(record.section)}
                  </div>
                )}
            </div>
          ),
        },
      );
    }

    return columns;
  }, [
    isClassificationModalOpen,
    showMore,
    handleArgumentSelection,
    handleArgumentEditClick,
  ]);

  return columns;
};

export const structureArgumentsDataForStage = (
  data: any[],
  targetStage: string,
  variant_consequence?: string,
  options?: StageFilterOptions,
): StructuredArgument[] => {
  // Filter data for the target stage and, for molecular_impact, by consequence
  const stageData = filterArgumentsByStage(
    data,
    targetStage,
    variant_consequence,
    options,
  );

  // Group data by section
  const groupedData = stageData.reduce((acc: Record<string, any[]>, item) => {
    const sectionId = item.section?.id?.toString() || 'unknown';
    if (!acc[sectionId]) {
      acc[sectionId] = [];
    }
    acc[sectionId].push(item);
    return acc;
  }, {});

  const structuredData: StructuredArgument[] = [];

  const processedSections = new Set<string>();

  Object.entries(groupedData).forEach(([sectionId, items]) => {
    const relevantItems = items;

    if (relevantItems.length > 0) {
      const sectionName = relevantItems[0].section?.display || '';
      const isNewSection = !processedSections.has(sectionName);
      processedSections.add(sectionName);

      if (isNewSection) {
        // Add section row
        structuredData.push({
          stage: CLASSIFICATION_STAGE_DISPLAY_NAMES[targetStage] || targetStage,
          section: sectionName,
          text: '',
          selected: false,
          acmg_class: '',
          acmg_grade: '',
          acmg_rule_code: '',
          isStageRow: true,
        });
      }

      relevantItems.forEach((item, index, array) => {
        structuredData.push({
          stage: CLASSIFICATION_STAGE_DISPLAY_NAMES[targetStage] || targetStage,
          section: item.section?.display || '',
          text: item.text || '',
          comment: item.comment,
          evidencedata: item.evidencedata,
          selected: item.is_argument_met === false ? false : item.selected,
          acmg_class: item.acmg_class || '',
          acmg_grade: item.acmg_grade || '',
          acmg_rule_code: item.acmg_rule_code || '',
          isFirstInStage: index === 0,
          isFirstInSection: isNewSection && index === 0,
          isLastInSection: index === array.length - 1,
          id: item.id,
          selection_type: item.selection_type,
          selection_type_creation_date: item.selection_type_creation_date,
          is_argument_met: item.is_argument_met,
        });
      });
    }
  });

  return structuredData;
};

export const structureArgumentsDataForInterpretationZone = (
  data: any[],
  targetStage?: string,
) => {
  // Filter data for the target stage
  const stageData = data.filter(
    (item) =>
      item.stage === targetStage &&
      item.selected &&
      item.is_argument_met !== false,
  );

  // Group data by section
  const groupedData = stageData.reduce((acc: Record<string, any[]>, item) => {
    const sectionId = item.section?.display?.toString() || '';
    if (!acc[sectionId]) {
      acc[sectionId] = [];
    }
    acc[sectionId].push(item);
    return acc;
  }, {});

  return groupedData;
};
